import { QueryFunctionContext } from 'react-query'
import request from 'services/axios'

/**
 * The server-side orders of «وام‌های درخواستی» (LoanDemandView.SORTS).
 *
 * - `oldest` — the member who has waited longest first; the queue's default
 * - `urgency` — فوریت blended with the wait: a request climbs from the day
 *   its member said the need begins, so an old «پایین» can pass a fresh «فوری»
 * - `agreement` — «بیشترین موافقت مدیران»; the server only honours it for a
 *   manager, since the order would otherwise publish the hidden vote
 *
 * رتبهٔ اعتباری is not here: it is a fund-wide ranking the client applies over
 * the whole queue (see tabs/requested).
 */
export type TDemandSort = 'oldest' | 'newest' | 'urgency' | 'agreement'

/** decided requests, by the date the card shows as the decision */
export type TDecidedSort = 'decided_newest' | 'decided_oldest'

export const getDemandLoans = async (
    pageParams: number,
    id: string | undefined,
    sort: TDemandSort = 'oldest'
) => {
    const { data }: { data: IDemandLoans } = await request.get(
        `/api/v1/moneypools/${id}/loan-demands/?page=${pageParams}&sort=${sort}`
    )
    return data
}

// `sort`, not the `order_bys`/`reverse` pair these used to send: the backend
// never read `reverse`, so «پذیرفته‌شده» asked for newest-first and showed the
// oldest acceptance — from a year and more ago — at the top.
export const getAcceptedDemandLoans = async (
    pageParams: number,
    id: string | undefined,
    sort: TDecidedSort = 'decided_newest'
) => {
    const { data }: { data: IAcceptedDemandLoans } = await request.get(
        `/api/v1/moneypools/${id}/loan-demands/?states=acpd&sort=${sort}&page=${pageParams}`
    )
    return data
}

export const getRejectedDemandLoans = async (
    pageParams: number,
    id: string | undefined,
    sort: TDecidedSort = 'decided_newest'
) => {
    const { data }: { data: IRejectedDemandLoans } = await request.get(
        `/api/v1/moneypools/${id}/loan-demands/?states=cnld%2Crjcd&sort=${sort}&page=${pageParams}`
    )
    return data
}

export const deleteRequestedLoan = async ({
    id,
    loanId,
    note,
}: {
    id: string | undefined
    loanId: number
    /**
     * A manager's reason for rejecting. The backend writes it onto the
     * request's timeline, where the member reads it — it is the only place
     * they ever learn why.
     */
    note?: string
}) => {
    const response = await request.delete(
        `/api/v1/moneypools/${id}/loan-demands/${loanId}/`,
        // The body itself: `request.delete` already puts its second argument
        // under axios's `data`. Wrapped once more, as it was until 1.29.4, the
        // server got {"data": {"note": …}}, read no `note`, and not one of the
        // 78 rejections before then kept the reason the manager typed.
        note ? { note } : undefined
    )
    return response
}

export const getCards = async (ctx: QueryFunctionContext) => {
    const { data }: { data: IGetCardsData } = await request.post(
        '/api/v1/payment/init/?os_type=android&app_version=1.892.4',
        [
            {
                id: ctx.queryKey[1],
                type: 'psp',
                amount: 0,
                cashin_type: 'cnp',
                paid_obj_id: 0,
            },
        ]
    )
    return data
}

export const AddRequestedLoan = async (payload: IAddRequestedLoadServise) => {
    const { data } = await request.post(
        `api/v1/moneypools/${payload.id}/loan-demands/`,
        {
            amount: payload.amount,
            num_repayments: payload.num_repayments,
            demand_priority: payload.demand_priority,
            user_description: payload.user_description,
            selected_card_number: payload.selected_card_number,
            poolship_id: payload.poolship_id,
            proposed_guarantors: payload.proposed_guarantors ?? [],
        }
    )
    return data
}

export const initTransaction = async (ctx: QueryFunctionContext) => {
    const { data }: { data: IInitTransaction } = await request.post(
        '/api/v1/payment/init/?os_type=android&app_version=1.891.1',
        [
            {
                id: ctx.queryKey[1],
                type: 'mbr',
                amount: 0,
                cashin_type: 'hwt',
                paid_obj_id: 0,
            },
        ]
    )
    return data
}

export const initTransactionForOthers = async (
    id: number,
    amount: number
): Promise<IInitTransaction> => {
    const { data } = await request.post(
        '/api/v1/payment/init/?os_type=android&app_version=1.892.12',
        [
            {
                id,
                type: 'psp',
                amount,
                cashin_type: 'cnp',
                paid_obj_id: 0,
            },
        ]
    )
    return data
}

export const initTransactionForOthersLoans = async (
    ctx: QueryFunctionContext
) => {
    const { data }: { data: IInitTransaction } = await request.post(
        '/api/v1/payment/init/?os_type=android&app_version=1.892.31',
        [
            {
                id: ctx.queryKey[1],
                type: 'psp',
                amount: ctx.queryKey[2],
                cashin_type: 'cnr',
                paid_obj_id: ctx.queryKey[3],
            },
        ]
    )
    return data
}

export interface IInstallmentToPay {
    id: number
    amount: number
    index?: number
}

/**
 * Quote a repayment covering one or more installments.
 *
 * `/api/v1/payment/init/` already accepts a list and prices every entry, so
 * settling five installments is one request, not five. The response's
 * `receivers` array comes back in the order sent (the view appends as it
 * walks the validated list), which is the only way to map a commission back
 * to its installment — the echo carries the poolship id, identical for every
 * row, not the `paid_obj_id`.
 */
export const initTransactionForInstallments = async (
    poolshipId: number,
    installments: IInstallmentToPay[]
) => {
    const { data }: { data: IInitTransaction } = await request.post(
        '/api/v1/payment/init/?os_type=android&app_version=1.892.31',
        installments.map((installment) => ({
            id: poolshipId,
            type: 'psp',
            amount: installment.amount,
            cashin_type: 'cnr',
            paid_obj_id: installment.id,
        }))
    )
    return data
}

/**
 * Record ONE installment as received by hand — «ثبت پرداخت».
 *
 * The body is deliberately empty. `LoanView.pay_installment` picks
 * `loan.unpaid_installments[0]` itself and overwrites amount, poolship and
 * type from it; sending no `transaction` is what makes it a RECORD cash-in,
 * which charges the fund's balance and marks the installment paid (owners and
 * managers only) and carries no commission.
 *
 * To record several, call this once per installment IN SEQUENCE — each call
 * takes whatever is now the earliest unpaid one, so overlapping calls would
 * race for the same row. Because the loan page's selection is always a prefix
 * of the unpaid installments, N calls settle exactly the N that were picked.
 */
export const recordInstallmentPayment = async (
    moneyPool: string | number,
    loanId: string | number,
    /**
     * The manager's note on this repayment — «توضیحات» on every other kind of
     * transaction. Sent as `tag`, the same column a recorded deposit or
     * withdrawal writes, so it shows up in the transaction's details and its
     * #hashtags are searchable alongside everything else.
     *
     * Only offered on the record path: a member paying through the gateway is
     * not asked to describe their own installment.
     */
    tag?: string
) => {
    const { data } = await request.post(
        `/api/v1/payment/moneypools/${moneyPool}/loans/${loanId}/pay-installment/`,
        tag ? { tag } : {}
    )
    return data
}

export const cashin = async (ctx: QueryFunctionContext) => {
    const { data }: { data: CashinTypes } = await request.post(
        `/api/v1/payment/moneypools/${ctx.queryKey[5]}/moneypool-cashins/`,
        [
            {
                poolship: ctx.queryKey[1],
                amount: ctx.queryKey[2],
                type: ctx.queryKey[3],
                tag: ctx.queryKey[4],
                transaction: {
                    ctx_id: ctx.queryKey[5],
                    ctx_type: 'mnp',
                    destination: ctx.queryKey[6],
                    gateway: ctx.queryKey[7],
                    is_group_pay: false,
                    payer_name: '',
                    receiver: ctx.queryKey[8],
                    receiver_name: '',
                    source: 'gtw',
                    state: '',
                    state_time: '',
                    transaction_code: '',
                },
            },
        ]
    )
    return data
}

export const cashinForMng = async (payload: IFuckingPayload) => {
    const { data }: { data: CashinTypes } = await request.post(
        `/api/v1/payment/moneypools/${payload.moneyPool}/moneypool-cashins/`,
        [
            {
                poolship: payload.poolship,
                amount: payload.amount,
                type: payload?.type,
                tag: payload?.tag ?? '',
            },
        ]
    )
    return data
}

export const getSubHeaderLoans = async (ctx: QueryFunctionContext) => {
    const { data }: { data: ISubHeaderLoans } = await request.get(
        `/api/v1/moneypools/${ctx.queryKey[1]}/loans/?poolship_id=${ctx.queryKey[2]}&page=${ctx.queryKey[3]}`
    )
    return data
}
